import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CreateAddOnPayload } from '../classes/create-addon-payload';
import { DropOffTask } from '../classes/dropoff-task';
import { EndDeleteTaskPayload } from '../classes/end-delete-task';
import { PickUpTask } from '../classes/pickup-task';
import { RemoteParkingAction } from '../classes/remote-parking-action-payload';
import { RemoveAddOnPayload } from '../classes/remove-addon-payload';
import { UnassignTaskPayload } from '../classes/unassign-task-payload';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class MyTasksService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpClient,
    private logger: LoggerService,
    private toastService: ToastService
  ) { }

  private getTaskUrl(taskId: string): string {
    const config = this.configurationService.configuration$.getValue();
    if (!config) {
      throw new Error('Configuration not loaded');
    }
    return config.HostApi.concat('/task/').concat(taskId);
  }

  markTaskAsStarted(payload: PickUpTask): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(payload.taskIds[0]);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to start task'))
    );
  }

  dropoffPassengers(payload: DropOffTask): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(payload.taskIds[0]);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to drop off passengers'))
    );
  }

  endOrDeleteTask(payload: EndDeleteTaskPayload): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(payload.taskId);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to end/delete task'))
    );
  }

  remoteParking(payload: RemoteParkingAction): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(payload.taskIds[0]);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to update remote parking'))
    );
  }

  unassignTask(payload: UnassignTaskPayload): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(payload.taskIds[0]);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to unassign task'))
    );
  }

  createAddOn(payload: CreateAddOnPayload, taskName: string): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(taskName);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to create add-on'))
    );
  }

  removeAddOn(payload: RemoveAddOnPayload, taskName: string): Observable<HttpResponse<any>>{
    const url = this.getTaskUrl(taskName);
    const headers = new HttpHeaders().set("Content-type", "application/json; charset=utf-8");
    return this.http.put<HttpResponse<any>>(url, JSON.stringify(payload), { "headers":headers, observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, 'Failed to remove add-on'))
    );
  }

  private handleError(error: HttpErrorResponse, message: string) {
    this.toastService.show('HTTP Error Occurred!', { classname: 'bg-danger text-light', delay: 8000 });
    if (error.status === 0) {
      this.logger.error(`${message}:`, error.error);
    } else {
      this.logger.error(`${message}: ${error.status}, body was: ${error.error}`);
    }
    return throwError(error);
  }
}
